/**
 * C4 stereotype subtitles — the small "[Software System]" / "[Container]"
 * / "[Component]" line that C4 diagrams put under the element name. Per
 * Simon Brown's notation guidance.
 *
 * Returns `null` when no stereotype applies (e.g. an ArchiMate-only
 * concept on an ArchiMate-only diagram). Callers should only display the
 * subtitle when the active diagram's view-kind is C4-flavoured (id
 * starts with `c4.`); the helper itself doesn't gate on that — it's
 * usable in tooltips and exports too.
 */
import { getRegistry } from "../../registry";

export function isC4ViewKind(viewKindId: string | null | undefined): boolean {
  return !!viewKindId && viewKindId.startsWith("c4.");
}

const STEREOTYPES_NL: Record<string, string> = {
  // ApplicationComponent — legacy `c4_level`-discriminator (pre-M5 data).
  // Nieuwe data zet stereotypes via concept.stereotypes; deze tabel blijft
  // als fallback voor ongemigreerde rijen tot M8 alle c4_level-data weg is.
  "archimate.ApplicationComponent.system": "Software-systeem",
  "archimate.ApplicationComponent.container": "Container",
  "archimate.ApplicationComponent.component": "Component",
  "archimate.ApplicationComponent.none": "Component",
  // ArchiMate-base-types die in C4-views default-stereotype-tekst krijgen.
  // Sinds 2026-05-06 hebben de C4-instance/Person/InfrastructureNode-types
  // eigen stereotypes — c4.Person etc. uit deze tabel verwijderd omdat
  // typeId nooit meer `c4.Person` etc. kan zijn (was extension, nu weg).
  "archimate.BusinessActor": "Persoon",
  "archimate.BusinessRole": "Persoon",
  // Merge-doelen: na ArchiMate 4 draagt de data het generieke type.
  "archimate.Role": "Persoon",
  "archimate.Node": "Deployment Node",
  "archimate.Device": "Device",
  "archimate.SystemSoftware": "System Software",
  "archimate.Grouping": "System Boundary",
  "archimate.ApplicationService": "Service",
  "archimate.Service": "Service",
  "archimate.ApplicationInterface": "Interface",
  "archimate.DataObject": "Database",
  "archimate.Artifact": "Artifact",
  "archimate.CommunicationNetwork": "Network",
  "archimate.TechnologyService": "Technology Service",
};

const STEREOTYPES_EN: Record<string, string> = {
  "archimate.ApplicationComponent.system": "Software System",
  "archimate.ApplicationComponent.container": "Container",
  "archimate.ApplicationComponent.component": "Component",
  "archimate.ApplicationComponent.none": "Component",
  "archimate.BusinessActor": "Person",
  "archimate.BusinessRole": "Person",
  "archimate.Role": "Person",
  "archimate.Node": "Deployment Node",
  "archimate.Device": "Device",
  "archimate.SystemSoftware": "System Software",
  "archimate.Grouping": "System Boundary",
  "archimate.ApplicationService": "Service",
  "archimate.Service": "Service",
  "archimate.ApplicationInterface": "Interface",
  "archimate.DataObject": "Database",
  "archimate.Artifact": "Artifact",
  "archimate.CommunicationNetwork": "Network",
  "archimate.TechnologyService": "Technology Service",
};

/**
 * Return the localized C4 stereotype string (without surrounding
 * brackets — the renderer adds those) for a given concept type +
 * attributes. Returns null when no stereotype is mapped.
 *
 * When the concept has a `technology` attribute set, that is appended
 * to the base stereotype with a colon — matching the canonical C4
 * format `[Container: Spring Boot]` / `[Component: Spring Bean]`
 * (see c4model.com/diagrams/component for the example).
 */
export function getC4Stereotype(
  typeId: string,
  attributes: Record<string, unknown> | undefined,
  lang: string,
  stereotypes?: readonly string[],
): string | null {
  // Sinds M5 zijn C4-elementen stereotypes op ArchiMate-base-types.
  // Resolve via concept.stereotypes met sub-stereotype-voorkeur (een
  // «Microservice» wint van «Container»). Stripped van guillemets
  // («…») omdat C4-conventie vierkante haken `[…]` is en de
  // ConceptNode die zelf om de tekst plaatst.
  if (stereotypes && stereotypes.length > 0) {
    const reg = getRegistry();
    const c4Ids = stereotypes.filter((id) => id.startsWith("c4."));
    if (c4Ids.length > 0) {
      // Sub-stereotype winnt: zoek eerst eentje met extends_stereotype
      // gezet (bv. c4.Microservice extends c4.Container). Anders neem
      // de eerste c4-stereotype.
      const sub = c4Ids.find((id) => reg.getStereotype(id)?.extends_stereotype);
      const winner = sub ?? c4Ids[0];
      const st = winner ? reg.getStereotype(winner) : undefined;
      if (st) {
        const labelObj = st.label;
        const raw = (lang === "en" ? labelObj.en : labelObj.nl) ?? labelObj.en ?? labelObj.nl ?? "";
        const stripped = raw.replace(/^[«]/, "").replace(/[»]$/, "").trim();
        if (stripped) {
          // Stereotype-specifieke discriminator-suffix. Conventie:
          // "[Container: Spring Boot 3.2]" / "[Webserver: nginx 1.25.3]"
          // — de meest karakteriserende attribute van het stereotype
          // wordt aan het label geplakt. Map per stereotype; optioneel
          // version-attr eraan plakken.
          const discriminatorMap: Record<string, { primary: string; version?: string }> = {
            "c4.Webserver": { primary: "webserver_type", version: "version" },
            "c4.Storage": { primary: "storage_provider" },
            "c4.CloudRegion": { primary: "cloud_provider" },
            "c4.Cluster": { primary: "cluster_type", version: "cluster_version" },
            "c4.Namespace": { primary: "namespace_kind" },
            "c4.VM": { primary: "vm_provider", version: "os_version" },
            "c4.Pod": { primary: "pod_kind" },
            "c4.ContainerRuntime": {
              primary: "runtime_type",
              version: "runtime_version",
            },
            "c4.Browser": { primary: "target_platform" },
            "c4.MobileDevice": { primary: "platform", version: "min_os_version" },
            "c4.DatabaseServer": { primary: "dbms_type", version: "dbms_version" },
            "c4.Workstation": { primary: "form_factor", version: "os_version" },
          };
          const winnerId = winner;
          const disc = winnerId ? discriminatorMap[winnerId] : undefined;
          if (disc) {
            const rawValue = attributes?.[disc.primary];
            if (typeof rawValue === "string" && rawValue.trim().length > 0) {
              // Enum-waarde naar localized option-label vertalen waar
              // mogelijk. Voor non-enum velden valt 't terug op de raw
              // string. Sub-stereotypes erven attrs van hun parent —
              // we zoeken in de effectieve schema-set.
              const attrSchema = (st.attributes ?? []).find((a) => a.name === disc.primary);
              const optionLabel = attrSchema?.option_labels?.[rawValue.trim()];
              const labelText = optionLabel
                ? ((lang === "en" ? optionLabel.en : optionLabel.nl) ??
                  optionLabel.en ??
                  optionLabel.nl ??
                  rawValue.trim())
                : rawValue.trim();
              const v = disc.version ? attributes?.[disc.version] : undefined;
              const versionSuffix =
                typeof v === "string" && v.trim().length > 0 ? ` ${v.trim()}` : "";
              return `${stripped}: ${labelText}${versionSuffix}`;
            }
            return stripped;
          }
          // Default-pad: tech-stack-suffix uit c4.Container/Component-
          // bundle (M5-canonical: `tech_stack_name`, met optionele
          // `tech_stack_version`). Legacy `attributes.technology` als
          // fallback voor pre-M5-data.
          const techName = attributes?.tech_stack_name;
          if (typeof techName === "string" && techName.trim().length > 0) {
            const v = attributes?.tech_stack_version;
            const versionSuffix =
              typeof v === "string" && v.trim().length > 0 ? ` ${v.trim()}` : "";
            return `${stripped}: ${techName.trim()}${versionSuffix}`;
          }
          const tech = attributes?.technology;
          if (typeof tech === "string" && tech.trim().length > 0) {
            return `${stripped}: ${tech.trim()}`;
          }
          return stripped;
        }
      }
    }
  }
  // Legacy-pad voor pre-M5-data zonder stereotypes (c4_level-attribuut).
  const map = lang === "en" ? STEREOTYPES_EN : STEREOTYPES_NL;
  let base: string | null;
  if (typeId === "archimate.ApplicationComponent") {
    const level = (attributes?.c4_level as string | undefined) ?? "container";
    base = map[`${typeId}.${level}`] ?? map[`${typeId}.container`] ?? null;
  } else {
    base = map[typeId] ?? null;
  }
  if (!base) return null;
  const tech = attributes?.technology;
  if (typeof tech === "string" && tech.trim().length > 0) {
    return `${base}: ${tech.trim()}`;
  }
  return base;
}
